import { Skeleton, Td, Tr, Link, Hide, Text } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'

import PfTrafficLightBlock from '../../elements/pfTrafficLightBlock'
import { InspectorChannel } from '../../Pages/buildings/issues'
import { tableBreak } from '../../styles/buildingsVariables'
import { issueIcon } from '../icon/issueIcon'

type BuildingRowProps = {
  locale: string
  defaultName: string
  name: string
  slug: string
  type: string
  floorArea: number
  floorAreaDisplay: number
  epcRating: string
  epcUrl: string
  thumbnail: string
  picture: string
  projectRef: string
  company: string
  id: string
  lastSeen?: Date
  buildingTransmitting: boolean
  // useSimpleDash: boolean
  tlData: {
    type: string
    value: number
    target: number
    units: string
  }[]
  loading: boolean
  showIssues: boolean
  channelIssues: InspectorChannel[]
}

const PfTableRow = ({
  locale,
  name,
  slug,
  lastSeen,
  buildingTransmitting,
  floorAreaDisplay,
  epcRating,
  epcUrl,
  tlData,
  channelIssues,
  loading,
}: BuildingRowProps) => {
  const createIssueIcon = (): JSX.Element => {
    if (!lastSeen) {
      return issueIcon(slug, 'spanner', 'Building is still being setup')
    }
    if (!buildingTransmitting) {
      return issueIcon(slug, 'error', 'Building has stopped transmitting')
    }

    if (
      channelIssues.some(
        (channel) =>
          channel.isImportant &&
          (channel.notTransmitting || channel.notCumulative),
      )
    ) {
      return issueIcon(slug, 'warn', 'Some channels have issues')
    }
    return <></>
  }

  return (
    <Tr>
      {/* Name */}
      <Td whiteSpace={'pre-wrap'}>
        <Skeleton isLoaded={!loading}>
          <Text display={'flex'} justifyContent={'space-between'} gap={1}>
            <Link as={RouterLink} to={`${slug}`} textColor="linkColor">
              {name}
            </Link>
            {createIssueIcon()}
          </Text>
        </Skeleton>
      </Td>

      {/* Details */}
      <Hide breakpoint={tableBreak.large}>
        <Td width={'20%'} whiteSpace={'pre-wrap'}>
          <Skeleton isLoaded={!loading}>
            {floorAreaDisplay ? (
              floorAreaDisplay.toLocaleString(locale, {
                maximumSignificantDigits: 5,
              })
            ) : (
              <i>N/A</i>
            )}
          </Skeleton>
        </Td>
      </Hide>
      <Hide breakpoint={tableBreak.huge}>
        <Td width={'20%'} whiteSpace={'pre-wrap'}>
          <Skeleton isLoaded={!loading}>
            {(epcUrl && (
              <Link href={epcUrl} textColor="linkColor">
                {epcRating}
              </Link>
            )) || <Text>{epcRating}</Text>}
          </Skeleton>
        </Td>
      </Hide>

      {/* Targets */}
      <Hide breakpoint={tableBreak.tiny}>
        {tlData.slice(0, 1).map((tlDatum) => (
          <Td key={tlDatum.type} whiteSpace={'pre-wrap'}>
            {PfTrafficLightBlock({
              type: tlDatum.type,
              value: tlDatum.value,
              target: tlDatum.target,
              units: tlDatum.units,
              loading,
            })}
          </Td>
        ))}
      </Hide>
      <Hide breakpoint={tableBreak.small}>
        {tlData.slice(1, 2).map((tlDatum) => (
          <Td key={tlDatum.type} whiteSpace={'pre-wrap'}>
            {PfTrafficLightBlock({
              type: tlDatum.type,
              value: tlDatum.value,
              target: tlDatum.target,
              units: tlDatum.units,
              loading,
            })}
          </Td>
        ))}
      </Hide>
      <Hide breakpoint={tableBreak.medium}>
        {tlData.slice(2, 3).map((tlDatum) => (
          <Td key={tlDatum.type} whiteSpace={'pre-wrap'}>
            {PfTrafficLightBlock({
              type: tlDatum.type,
              value: tlDatum.value,
              target: tlDatum.target,
              units: tlDatum.units,
              loading,
            })}
          </Td>
        ))}
      </Hide>
    </Tr>
  )
}

export default PfTableRow
